import { useEffect, useMemo, useState } from 'react';
import { DataGrid } from '@/components/data-grid/core/DataGrid';
import { KeenIcon, Modal } from '@/components';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import {
  useGetHarborRequestsQuery,
  useApproveHarborRequestMutation,
  useRejectHarborRequestMutation,
} from '@/services/HarborManagementService';
import { PaginationState } from '@tanstack/react-table';
import { HarborRequestViewModal } from './HarborRequestViewModal';
import { usePermissions } from '@/hooks/usePermissions';
import { HarborRequestData } from '@/types/harbor-requests.types';
import { ConfirmationDialog } from '@/components/dialogBox/ConfirmationDialog';

export const HarborRequestListings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { resetSearch } = usePagination();
  const { hasPermission } = usePermissions();

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    id?: string;
  }>({ isOpen: false });

  const [confirmState, setConfirmState] = useState<{
    isOpen: boolean;
    id: string | null;
    action: 'approve' | 'reject' | null;
  }>({ isOpen: false, id: null, action: null });

  const { data, isLoading, isFetching, refetch } = useGetHarborRequestsQuery(
    {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      search: debouncedSearchTerm.trim() || undefined,
    },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  const [approveHarborRequest] = useApproveHarborRequestMutation();
  const [rejectHarborRequest] = useRejectHarborRequestMutation();

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination((p) => ({ ...p, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    refetch();
  }, [debouncedSearchTerm, pagination.pageIndex, pagination.pageSize, refetch]);

  const rows: HarborRequestData[] = useMemo(() => data?.data || [], [data]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const resetFilters = () => {
    if (searchTerm) resetSearch();
    setSearchTerm('');
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  };

  const onConfirm = async () => {
    if (!confirmState.id || !confirmState.action) return;
    try {
      if (confirmState.action === 'approve') {
        const call = approveHarborRequest(confirmState.id);
        if (call && typeof call.unwrap === 'function') {
          await call.unwrap();
        } else {
          await call;
        }
        enqueueSnackbar('Request approved', { variant: 'success' });
      } else {
        const call = rejectHarborRequest({ id: confirmState.id });
        if (call && typeof call.unwrap === 'function') {
          await call.unwrap();
        } else {
          await call;
        }
        enqueueSnackbar('Request rejected', { variant: 'success' });
      }
      setConfirmState({ isOpen: false, id: null, action: null });
      refetch();
    } catch (e: unknown) {
      const error = e as { data?: { message?: string } };
      enqueueSnackbar(error?.data?.message || 'Action failed', {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        header: 'Location',
        accessorKey: 'location',
        cell: ({ row }: { row: { original: HarborRequestData } }) =>
          row.original.location || '-',
      },
      {
        header: 'Contact Email',
        accessorKey: 'contact_email',
      },
      {
        header: 'Website',
        accessorKey: 'website',
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: HarborRequestData } }) => (
          <div className="flex items-center gap-2">
            <button
              data-testid={`view-${row.original.id}`}
              onClick={() =>
                setModalState({ isOpen: true, id: row.original.id })
              }
              className="btn btn-sm btn-icon btn-light btn-outline"
              aria-label={`View ${row.original.name}`}
            >
              <KeenIcon type="filled" icon="eye" />
            </button>
            {hasPermission('harbor_management_update') && (
              <>
                <button
                  data-testid={`approve-${row.original.id}`}
                  onClick={() =>
                    setConfirmState({
                      isOpen: true,
                      id: row.original.id,
                      action: 'approve',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Approve ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="check" />
                </button>
                <button
                  data-testid={`reject-${row.original.id}`}
                  onClick={() =>
                    setConfirmState({
                      isOpen: true,
                      id: row.original.id,
                      action: 'reject',
                    })
                  }
                  className="btn btn-sm btn-icon btn-light btn-outline"
                  aria-label={`Reject ${row.original.name}`}
                >
                  <KeenIcon type="filled" icon="cross" />
                </button>
              </>
            )}
          </div>
        ),
        meta: { className: 'w-[160px]' },
      },
    ],
    [hasPermission]
  );

  return (
    <div className="space-y-4">
      <div className="card card-grid min-w-full">
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-harbor-requests"
              aria-label="Search harbor requests"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-harbor-requests"
                placeholder="Search harbor requests"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search harbor requests by name"
              />
            </label>
          </div>
          {searchTerm !== '' && (
            <div className="flex items-center">
              <button
                className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                onClick={resetFilters}
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <KeenIcon
                  type="solid"
                  icon="arrows-circle"
                  className="text-gray-600"
                />
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="p-4">
          {isLoading || isFetching ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems: data?.totalItems || 0,
                totalPages: data?.totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetFilters}
              hasActiveFilters={searchTerm !== ''}
            />
          )}
        </div>
      </div>

      <Modal
        open={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false })}
        title="View Harbor Request"
        disableEnforceFocus={false}
      >
        {modalState.id ? (
          <HarborRequestViewModal
            id={modalState.id}
            onClose={() => setModalState({ isOpen: false })}
          />
        ) : (
          <div />
        )}
      </Modal>

      <ConfirmationDialog
        open={confirmState.isOpen}
        onClose={() =>
          setConfirmState({ isOpen: false, id: null, action: null })
        }
        onConfirm={onConfirm}
        message={
          confirmState.action === 'approve'
            ? 'Are you sure you want to approve this harbor request?'
            : 'Are you sure you want to reject this harbor request?'
        }
        confirmButtonText={
          confirmState.action === 'approve' ? 'Approve' : 'Reject'
        }
      />
    </div>
  );
};
